import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import OfflinePinIcon from "@mui/icons-material/OfflinePin";
import { toast } from "react-toastify";

import useAxiosPrivate from "../../common/useAxiosPrivate";
import MultiSubheaderTableRowComponent from "../CustomComponents/SubheaderTableRowComponent";
import { Checklist } from "../CustomComponents/ChecklistInterface";
import googleDriveAxios from "../../common/googleDriveAxios";

interface SealingChecklistProps {
  chassisNo: string;
  setModel: React.Dispatch<React.SetStateAction<string>>;
  model: string;
  setCheckedBy: React.Dispatch<React.SetStateAction<string>>;
  isDisabled: boolean;
}

const SealingChecklist: React.FC<SealingChecklistProps> = ({
  chassisNo,
  setModel,
  model,
  setCheckedBy,
  isDisabled,
}) => {
  const axiosPrivate = useAxiosPrivate();
  const [loading, setLoading] = useState(false);
  const [externalFrontItems, setExternalFrontItems] = useState<Checklist[]>([]);
  const [externalDoorSideItems, setExternalDoorSideItems] =
    useState<Checklist[]>([]);
  const [externalRearItems, setExternalRearItems] = useState<Checklist[]>([]);
  const [externalRoadSideItems, setExternalRoadSideItems] =
    useState<Checklist[]>([]);
  const [internalEntranceItems, setInternalEntranceItems] =
    useState<Checklist[]>([]);
  const [internalEnsuiteItems, setInternalEnsuiteItems] =
    useState<Checklist[]>([]);
  const [internalShowerItems, setInternalShowerItems] =
    useState<Checklist[]>([]);
  const [internalKitchenItems, setInternalKitchenItems] =
    useState<Checklist[]>([]);
  const [internalDinnetteItems, setInternalDinnetteItems] =
    useState<Checklist[]>([]);
  const [internalBedroomItems, setInternalBedroomItems] =
    useState<Checklist[]>([]);
  const [finalCheckItems, setFinalCheckItems] = useState<Checklist[]>([]);
  const [checkedBy, setCheckedByState] = useState<string>("");
  const [date, setDate] = useState<string>("");
  const [isRadioChanged, setIsRadioChanged] = useState(false);
  const [completeStatus, setCompleteStatus] = useState("Incomplete");
  const [apiError, setApiError] = useState(false);
  const [activeCheckpointList, setActiveCheckpointList] = useState<any[]>([]);

  const allChecklists = useMemo(
    () => [
      ...externalFrontItems,
      ...externalDoorSideItems,
      ...externalRearItems,
      ...externalRoadSideItems,
      ...internalEntranceItems,
      ...internalEnsuiteItems,
      ...internalShowerItems,
      ...internalKitchenItems,
      ...internalDinnetteItems,
      ...internalBedroomItems,
      ...finalCheckItems,
    ],
    [
      externalFrontItems,
      externalDoorSideItems,
      externalRearItems,
      externalRoadSideItems,
      internalEntranceItems,
      internalEnsuiteItems,
      internalShowerItems,
      internalKitchenItems,
      internalDinnetteItems,
      internalBedroomItems,
      finalCheckItems,
    ]
  );

  const checkCheckpointRecord = useCallback(async () => {
    if (!chassisNo) return;
    try {
      const queryParams = new URLSearchParams({
        chassis_no: "DC" + chassisNo,
        dept_name: "sealing-quality-inspection",
      }).toString();

      const response = await googleDriveAxios.post(
        `/checkpoint-image-record?${queryParams}`
      );
      setActiveCheckpointList(response.data.recorded_checkpoint_list);
    } catch (error) {
      console.error("Error checking images:", error);
    }
  }, [chassisNo]);

  useEffect(() => {
    checkCheckpointRecord();
  }, [checkCheckpointRecord]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (isRadioChanged) {
        event.preventDefault();
        event.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isRadioChanged]);

  const resetState = useCallback(() => {
    setExternalFrontItems([]);
    setExternalDoorSideItems([]);
    setExternalRearItems([]);
    setExternalRoadSideItems([]);
    setInternalEntranceItems([]);
    setInternalEnsuiteItems([]);
    setInternalShowerItems([]);
    setInternalKitchenItems([]);
    setInternalDinnetteItems([]);
    setInternalBedroomItems([]);
    setFinalCheckItems([]);
    setModel("");
  setCheckedByState("");
    setCheckedBy("");
    setDate("");
    setCompleteStatus("Incomplete");
    setIsRadioChanged(false);
  }, [setModel, setCheckedBy]);

  const fetchData = useCallback(async () => {
    if (!chassisNo || chassisNo.length < 4 || chassisNo.length > 6) {
      resetState();
      setApiError(false);
      return;
    }

    setLoading(true);
    setApiError(false);
    setIsRadioChanged(false);

    try {
      const response = await axiosPrivate.post(
        `/prepare_sealing_quality_inspection/${chassisNo}`
      );
      const data = response.data;

      const mapChecklist = (items: Checklist[] = []) =>
        items.map((item) => ({
          ...item,
          status: item.status ? item.status.trim().toLowerCase() : "not checked",
        }));

      const externalFrontData = mapChecklist(data.externalFrontVanItems);
      const externalDoorSideData = mapChecklist(data.externalDoorSideVanItems);
      const externalRearData = mapChecklist(data.externalRearVanItems);
      const externalRoadSideData = mapChecklist(data.externalRoadSideVanItems);
      const internalEntranceData = mapChecklist(data.internalEntranceItems);
      const internalEnsuiteData = mapChecklist(data.internalEnsuiteItems);
      const internalShowerData = mapChecklist(data.internalShowerItems);
      const internalKitchenData = mapChecklist(data.internalKitchenItems);
      const internalDinnetteData = mapChecklist(data.internalDinnetteItems);
      const internalBedroomData = mapChecklist(data.internalBedRoomItems);
      const finalCheckData = mapChecklist(data.finalCheckItems);

      setExternalFrontItems(externalFrontData);
      setExternalDoorSideItems(externalDoorSideData);
      setExternalRearItems(externalRearData);
      setExternalRoadSideItems(externalRoadSideData);
      setInternalEntranceItems(internalEntranceData);
      setInternalEnsuiteItems(internalEnsuiteData);
      setInternalShowerItems(internalShowerData);
      setInternalKitchenItems(internalKitchenData);
      setInternalDinnetteItems(internalDinnetteData);
      setInternalBedroomItems(internalBedroomData);
      setFinalCheckItems(finalCheckData);

      setModel(data.model || "");
  setCheckedByState(data.checkedBy || "");
      setCheckedBy(data.checkedBy || "");

      const combined = [
        ...externalFrontData,
        ...externalDoorSideData,
        ...externalRearData,
        ...externalRoadSideData,
        ...internalEntranceData,
        ...internalEnsuiteData,
        ...internalShowerData,
        ...internalKitchenData,
        ...internalDinnetteData,
        ...internalBedroomData,
        ...finalCheckData,
      ];

      const isComplete =
        combined.length > 0 &&
        combined.every((item) =>
          ["pass", "fail", "n/a"].includes(item.status || "not checked")
        );
      setCompleteStatus(isComplete ? "Complete" : "Incomplete");

      if (data.createdOn) {
        const isoDate = data.createdOn.split("T")[0];
        setDate(isoDate);
      } else {
        setDate("");
      }
    } catch (error: any) {
      if (error.response?.status === 500) {
        setApiError(true);
      } else {
        toast.error("Failed to fetch sealing checklist.");
      }
    } finally {
      setLoading(false);
    }
  }, [axiosPrivate, chassisNo, resetState, setCheckedBy, setModel]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const saveData = async () => {
    if (!date.trim()) {
      toast.error("Date is required.");
      return;
    }
    if (!checkedBy.trim()) {
      toast.error("Checked By field is required.");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        chassisNo,
        model,
        externalFrontVanItems: externalFrontItems.map((item) => ({
          title: item.title,
          notes: item.notes,
          status: item.status,
        })),
        externalDoorSideVanItems: externalDoorSideItems.map((item) => ({
          title: item.title,
          notes: item.notes,
          status: item.status,
        })),
        externalRearVanItems: externalRearItems.map((item) => ({
          title: item.title,
          notes: item.notes,
          status: item.status,
        })),
        externalRoadSideVanItems: externalRoadSideItems.map((item) => ({
          title: item.title,
          notes: item.notes,
          status: item.status,
        })),
        internalEntranceItems: internalEntranceItems.map((item) => ({
          title: item.title,
          notes: item.notes,
          status: item.status,
        })),
        internalEnsuiteItems: internalEnsuiteItems.map((item) => ({
          title: item.title,
          notes: item.notes,
          status: item.status,
        })),
        internalShowerItems: internalShowerItems.map((item) => ({
          title: item.title,
          notes: item.notes,
          status: item.status,
        })),
        internalKitchenItems: internalKitchenItems.map((item) => ({
          title: item.title,
          notes: item.notes,
          status: item.status,
        })),
        internalDinnetteItems: internalDinnetteItems.map((item) => ({
          title: item.title,
          notes: item.notes,
          status: item.status,
        })),
        internalBedRoomItems: internalBedroomItems.map((item) => ({
          title: item.title,
          notes: item.notes,
          status: item.status,
        })),
        finalCheckItems: finalCheckItems.map((item) => ({
          title: item.title,
          notes: item.notes,
          status: item.status,
        })),
        checkedBy,
        createdOn: date,
      };

      await toast.promise(
        axiosPrivate.post(
          "/create_or_update_sealing_quality_inspection",
          payload
        ),
        {
          pending: "Saving data...",
          success: "Data saved successfully!",
          error: "Failed to save data.",
        }
      );

      setIsRadioChanged(false);
      await fetchData();

      if (checkedBy) {
        const combinedChecklists = [
          ...externalFrontItems,
          ...externalDoorSideItems,
          ...externalRearItems,
          ...externalRoadSideItems,
          ...internalEntranceItems,
          ...internalEnsuiteItems,
          ...internalShowerItems,
          ...internalKitchenItems,
          ...internalDinnetteItems,
          ...internalBedroomItems,
          ...finalCheckItems,
        ];

        let itemNoCounter = 1;
        const newReworkItems = combinedChecklists
          .filter((item) => item.status === "fail")
          .flatMap((item) => {
            const title = item.title || "No Title";
            const notesStr = item.notes || "";

            if (notesStr.includes(";")) {
              const parts = notesStr
                .split(";")
                .map((part) => part.trim())
                .filter(Boolean);
              return parts.map((note) => ({
                itemNo: itemNoCounter++,
                problemDescription: `${title}: ${note}`,
                comment: "",
                whoFoundFault: checkedBy,
                responsible: "",
                whoDidRework: "",
                timeSpentOnRework: { hours: 0, minutes: 0, seconds: 0 },
                completed: false,
              }));
            }

            const description = notesStr.trim()
              ? `${title}: ${notesStr.trim()}`
              : title;
            return [
              {
                itemNo: itemNoCounter++,
                problemDescription: description,
                comment: "",
                whoFoundFault: checkedBy,
                responsible: "",
                whoDidRework: "",
                timeSpentOnRework: { hours: 0, minutes: 0, seconds: 0 },
                completed: false,
              },
            ];
          });

        if (newReworkItems.length > 0) {
          const reworkPayload = {
            chassisNo,
            model,
            station_name: "sealing-quality-inspection",
            reworkItems: newReworkItems,
          };

          await axiosPrivate.post(`rework/save`, reworkPayload, {
            params: { rework_type: "sealing-quality-inspection" },
          });
        }
      }
    } catch (error) {
      console.error("Error saving sealing checklist:", error);
      toast.error("Failed to save data.");
    } finally {
      setLoading(false);
    }
  };

  const handleNotesChange = (
    index: number,
    value: string,
    type:
      | "external_front"
      | "external_door_side"
      | "external_rear"
      | "external_road_side"
      | "internal_entrance"
      | "internal_ensuite"
      | "internal_shower"
      | "internal_kitchen"
      | "internal_dinnette"
      | "internal_bedroom"
      | "final_check"
  ) => {
    if (completeStatus === "Complete") return;

    const updateChecklist = (
      checklist: Checklist[],
      setChecklist: React.Dispatch<React.SetStateAction<Checklist[]>>
    ) => {
      const updatedData = [...checklist];
      updatedData[index].notes = value;
      setChecklist(updatedData);
    };

    switch (type) {
      case "external_front":
        updateChecklist(externalFrontItems, setExternalFrontItems);
        break;
      case "external_door_side":
        updateChecklist(externalDoorSideItems, setExternalDoorSideItems);
        break;
      case "external_rear":
        updateChecklist(externalRearItems, setExternalRearItems);
        break;
      case "external_road_side":
        updateChecklist(externalRoadSideItems, setExternalRoadSideItems);
        break;
      case "internal_entrance":
        updateChecklist(internalEntranceItems, setInternalEntranceItems);
        break;
      case "internal_ensuite":
        updateChecklist(internalEnsuiteItems, setInternalEnsuiteItems);
        break;
      case "internal_shower":
        updateChecklist(internalShowerItems, setInternalShowerItems);
        break;
      case "internal_kitchen":
        updateChecklist(internalKitchenItems, setInternalKitchenItems);
        break;
      case "internal_dinnette":
        updateChecklist(internalDinnetteItems, setInternalDinnetteItems);
        break;
      case "internal_bedroom":
        updateChecklist(internalBedroomItems, setInternalBedroomItems);
        break;
      case "final_check":
        updateChecklist(finalCheckItems, setFinalCheckItems);
        break;
      default:
        break;
    }
  };

  const handleRadioChange = (
    index: number,
    value: string,
    type:
      | "external_front"
      | "external_door_side"
      | "external_rear"
      | "external_road_side"
      | "internal_entrance"
      | "internal_ensuite"
      | "internal_shower"
      | "internal_kitchen"
      | "internal_dinnette"
      | "internal_bedroom"
      | "final_check"
  ) => {
    if (completeStatus === "Complete") return;
    setIsRadioChanged(true);

    const updateChecklist = (
      checklist: Checklist[],
      setChecklist: React.Dispatch<React.SetStateAction<Checklist[]>>
    ) => {
      const updatedData = [...checklist];
      updatedData[index].status = value.toLowerCase();
      setChecklist(updatedData);
    };

    switch (type) {
      case "external_front":
        updateChecklist(externalFrontItems, setExternalFrontItems);
        break;
      case "external_door_side":
        updateChecklist(externalDoorSideItems, setExternalDoorSideItems);
        break;
      case "external_rear":
        updateChecklist(externalRearItems, setExternalRearItems);
        break;
      case "external_road_side":
        updateChecklist(externalRoadSideItems, setExternalRoadSideItems);
        break;
      case "internal_entrance":
        updateChecklist(internalEntranceItems, setInternalEntranceItems);
        break;
      case "internal_ensuite":
        updateChecklist(internalEnsuiteItems, setInternalEnsuiteItems);
        break;
      case "internal_shower":
        updateChecklist(internalShowerItems, setInternalShowerItems);
        break;
      case "internal_kitchen":
        updateChecklist(internalKitchenItems, setInternalKitchenItems);
        break;
      case "internal_dinnette":
        updateChecklist(internalDinnetteItems, setInternalDinnetteItems);
        break;
      case "internal_bedroom":
        updateChecklist(internalBedroomItems, setInternalBedroomItems);
        break;
      case "final_check":
        updateChecklist(finalCheckItems, setFinalCheckItems);
        break;
      default:
        break;
    }
  };

  const checklistSections = [
    {
      data: externalFrontItems,
      type: "external_front" as const,
      subHeaderText: "External-Front Of Van",
    },
    {
      data: externalDoorSideItems,
      type: "external_door_side" as const,
      subHeaderText: "External-Door Side Of Van",
    },
    {
      data: externalRearItems,
      type: "external_rear" as const,
      subHeaderText: "External-Rear Of Van",
    },
    {
      data: externalRoadSideItems,
      type: "external_road_side" as const,
      subHeaderText: "External Road Side Of Van",
    },
    {
      data: internalEntranceItems,
      type: "internal_entrance" as const,
      subHeaderText: "Internal-Entrance",
    },
    {
      data: internalEnsuiteItems,
      type: "internal_ensuite" as const,
      subHeaderText: "Internal-Ensuite",
    },
    {
      data: internalShowerItems,
      type: "internal_shower" as const,
      subHeaderText: "Internal-Shower",
    },
    {
      data: internalKitchenItems,
      type: "internal_kitchen" as const,
      subHeaderText: "Internal-Kitchen",
    },
    {
      data: internalDinnetteItems,
      type: "internal_dinnette" as const,
      subHeaderText: "Internal-Dinnette",
    },
    {
      data: internalBedroomItems,
      type: "internal_bedroom" as const,
      subHeaderText: "Internal-Bedroom",
    },
    {
      data: finalCheckItems,
      type: "final_check" as const,
      subHeaderText: "Final Check",
    },
  ];

  const renderTableRows = () => {
    let serialNumberCounter = 0;
    if (loading) {
      return (
        <TableRow>
          <TableCell colSpan={5} align="center">
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                zIndex: 10,
              }}
            >
              <CircularProgress />
            </Box>
          </TableCell>
        </TableRow>
      );
    }

    if (apiError) {
      return (
        <TableRow>
          <TableCell colSpan={5} align="center">
            <Typography color="error">Invalid DC Number</Typography>
          </TableCell>
        </TableRow>
      );
    }

    return (
      <React.Fragment>
        {checklistSections.map((section) => {
          const serialNumberStart = serialNumberCounter + 1;
          serialNumberCounter += section.data.length;

          return (
            <MultiSubheaderTableRowComponent
              key={section.type}
              subHeaderText={section.subHeaderText}
              type={section.type}
              data={section.data}
              handleNotesChange={(index, value) =>
                handleNotesChange(index, value, section.type)
              }
              handleRadioChange={(index, value) =>
                handleRadioChange(index, value, section.type)
              }
              disabled={completeStatus === "Complete" || isDisabled}
              allNotChecked={
                allChecklists.length > 0 &&
                allChecklists.every(
                  (item) => (item.status || "not checked").toLowerCase() === "not checked"
                )
              }
              serialNumberStart={serialNumberStart}
              chassisDCNumber={"DC" + chassisNo}
              deptName="sealing-quality-inspection"
              activeCheckpointList={activeCheckpointList}
              checkCheckpointRecord={checkCheckpointRecord}
            />
          );
        })}
      </React.Fragment>
    );
  };

  return (
    <Box
      sx={{
        position: "relative",
        filter: loading ? "blur(5px)" : "none",
        pointerEvents: loading ? "none" : "auto",
      }}
    >
      <Typography
        sx={{
          mb: 2,
          fontWeight: 600,
          display: "flex",
          alignItems: "center",
          gap: "5px",
          color:
            completeStatus === "Incomplete" ? "error.main" : "success.main",
        }}
      >
        {completeStatus === "Incomplete" ? (
          <>⚠️ INCOMPLETE</>
        ) : (
          <>
            <OfflinePinIcon /> COMPLETE
          </>
        )}
      </Typography>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow className="table__head_row">
              <TableCell
                className="table__head_cell"
                sx={{
                  width: "1%",
                  textAlign: "center",
                  verticalAlign: "middle",
                  padding: "0px",
                }}
              ></TableCell>
              <TableCell
                className="table__head_cell"
                sx={{
                  width: "1%",
                  textAlign: "center",
                  verticalAlign: "middle",
                  padding: "0px",
                }}
              >
                S/N
              </TableCell>
              <TableCell className="table__head_cell" sx={{ width: "20%" }}>
                CHECKLIST
              </TableCell>
              <TableCell className="table__head_cell" sx={{ width: "20%" }}>
                NOTES
              </TableCell>
              <TableCell
                className="table__head_cell"
                sx={{ width: "5%" }}
                align="center"
              >
                STATUS
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{renderTableRows()}</TableBody>
        </Table>
      </TableContainer>

      <Grid container spacing={3} my={4}>
        <Grid item md={4} sm={6} xs={12}>
          <TextField
            label="Checked By"
            value={checkedBy}
            onChange={(e) => {
              if (completeStatus === "Complete") return;
              setCheckedByState(e.target.value);
              setCheckedBy(e.target.value);
            }}
            fullWidth
            disabled={completeStatus === "Complete"}
          />
        </Grid>
        <Grid item md={4} sm={6} xs={12}>
          <TextField
            label="Date"
            type="date"
            value={date}
            onChange={(e) => {
              if (completeStatus === "Complete") return;
              setDate(e.target.value);
            }}
            fullWidth
            InputLabelProps={{ shrink: true }}
            disabled={completeStatus === "Complete"}
          />
        </Grid>
      </Grid>

      <Button
        variant="contained"
        color="primary"
        onClick={saveData}
        disabled={
          allChecklists.length === 0 ||
          apiError ||
          loading ||
          (!chassisNo && !model) ||
          completeStatus === "Complete" ||
          isDisabled
        }
      >
        Save
      </Button>
    </Box>
  );
};

export default SealingChecklist;
